import cn from 'classnames';
import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { trackEditorialImpression, trackEditorialClick } from './helpers/EditorialTracking.ts';
import { sanitizeAndDecodeHtml } from '../helpers/htmlHelpers.ts';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedEditorialModule.module.css';

import { type HpSharedEditorialModule_componentModule$key } from './__generated__/HpSharedEditorialModule_componentModule.graphql.ts';

const MIN_EDITORIAL_ITEMS_COUNT = 4;
const ITEMS_TO_SHOW_RESP = 3.5;
const ITEMS_TO_SHOW_MOBILE = 1.2;

const componentModuleFragment = graphql`
    fragment HpSharedEditorialModule_componentModule on EditorialModule {
        title
        items(userType: $userType) {
            imageUrl
            title
            cta
            linkData {
                path
            }
        }
    }
`;

export const HpSharedEditorialModule: FC<{
    componentModule: HpSharedEditorialModule_componentModule$key;
    isMobile: boolean;
}> = ({ componentModule: componentModuleRef, isMobile }) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);

    const { title: moduleTitle, items } = componentModule || {};
    const modules = (items || []).filter(filterNulls);

    if (modules.length < MIN_EDITORIAL_ITEMS_COUNT) {
        return null;
    }

    return (
        <>
            <div
                className={cn({
                    [dibsCss.pyLarge]: isMobile,
                    [dibsCss.pyLarger]: !isMobile,
                })}
                data-tn="editorial-section"
            >
                {moduleTitle && (
                    <div
                        className={cn(dibsCss.textCenter, {
                            [dibsCss.mbSmall]: isMobile,
                            [dibsCss.mbMedium]: !isMobile,
                            [dibsCss.sassyFontHeaderLarge]: isMobile,
                            [dibsCss.sassyFontHeaderXxLarge]: !isMobile,
                        })}
                    >
                        {moduleTitle}
                    </div>
                )}
                <div className={isMobile ? 'primary-viewport-width' : undefined}>
                    <ScrollCarousel
                        enableScroll
                        showArrows={!isMobile}
                        itemsToShow={isMobile ? ITEMS_TO_SHOW_MOBILE : ITEMS_TO_SHOW_RESP}
                        scrollPadding={isMobile ? 'small' : 'none'}
                        gapSize="smaller"
                        isFullWidth={!isMobile}
                        arrowClassName={!isMobile ? styles.arrow : undefined}
                    >
                        {modules.map((item, index) => {
                            const { imageUrl, title, cta, linkData } = item;
                            const itemTitle = sanitizeAndDecodeHtml(title);

                            return (
                                <div key={index} className={dibsCss.wFull}>
                                    <VisibilityTracker
                                        onVisibilityChange={({ isVisible }) => {
                                            if (isVisible) {
                                                trackEditorialImpression({
                                                    item,
                                                    index,
                                                });
                                            }
                                        }}
                                    />
                                    <Link
                                        href={linkData?.path || ''}
                                        className={styles.text}
                                        onClick={() => trackEditorialClick({ item, index })}
                                    >
                                        <img
                                            alt={itemTitle}
                                            src={imageUrl || ''}
                                            srcSet={getSrcsetString(imageUrl || '', [500, 640])}
                                            sizes="(max-width: 568px) 500px, 640px"
                                            className={cn(dibsCss.wFull, {
                                                [dibsCss.mbXsmall]: isMobile,
                                                [dibsCss.mbSmaller]: !isMobile,
                                            })}
                                        />
                                        <div
                                            className={cn(
                                                dibsCss.sassyFontHeaderMedium,
                                                dibsCss.mbXxsmall
                                            )}
                                        >
                                            {itemTitle}
                                        </div>
                                        <div
                                            className={cn(
                                                dibsCss.sassyFontBodyMediumNormal,
                                                styles.linkText
                                            )}
                                        >
                                            {cta}
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </ScrollCarousel>
                </div>
            </div>
        </>
    );
};
